import React, { FC, useMemo } from 'react';

import {
	EvSection,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { MOBILE_SDK_TEST_FILTERS_SECTION_ID } from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { useMobileSdkSingleTestFilters } from 'src/apps/mobile-sdk/hooks/useMobileSdkSingleTestFilters';
import { useMobileSdkTestFilterOptionsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestFilterOptionsQuery';
import { TMobileSdkTestFilterOptions } from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { ProductFiltersPanel } from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapPanelValuesToFilters,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';

import './MobileSdkSingleTestFiltersSection.scss';

interface IMobileSdkSingleTestFiltersProps {
	testId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
}

export const MobileSdkSingleTestFiltersSection: FC<IMobileSdkSingleTestFiltersProps> = ({
	testId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel
}) => {
	const filterOptionsQuery = useMobileSdkTestFilterOptionsQuery(testId);

	const { filterDefinitions, mergedFilterOptions } = useMobileSdkSingleTestFilters({
		allLabels: filterOptionsQuery.data?.scans ?? [],
		selectedLabels: ((filters as TMobileSdkTestFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(filterDefinitions),
		[filterDefinitions]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	const handleApply = (appliedFilters: TEvFiltersPanelValues): void => {
		onFilterChange(tableId, mapPanelValuesToFilters(appliedFilters));
	};

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MOBILE_SDK_TEST_FILTERS_SECTION_ID}
			className="mobile-sdk-single-test-filters-section"
		>
			<ProductFiltersPanel
				appElement={PLATFORM_APP_ID}
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				filterDefinitions={panelDefinitions}
			/>
		</EvSection>
	);
};
